import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import SidebarHeader from './SidebarHeader'
import SidebarMenuItem from './SidebarMenuItem'
import ThemeToggle from './ThemeToggle'
import SidebarUserBlock from './SidebarUserBlock'
import SidebarCollapseButton from './SidebarCollapseButton'
import { useAppTheme } from '../../theme/ThemeContext'
import { useAuth } from '@/auth/AuthContext'
import {
  ICON_CELL,
  ICON_INSET,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_OPEN_WIDTH,
  SIDEBAR_TRANSITION,
  SIDEBAR_TRANSITION_MS,
  reducedMotion,
} from './sidebarMotion'

// Import SVG icons from assets
import HomeIcon from '../../assets/siderbar_icons/Home.svg'
import MapIcon from '../../assets/siderbar_icons/Map.svg'
import CircleIcon from '../../assets/siderbar_icons/Circle.svg'
import SettingIcon from '../../assets/siderbar_icons/Setting.svg'
import NotificationsIcon from '../../assets/siderbar_icons/Notifications.svg'
import HistoryChangesIcon from '../../assets/siderbar_icons/HistoryChanges.svg'
import SensorsIcon from '../../assets/siderbar_icons/Sensors.svg'
import SupportIcon from '../../assets/siderbar_icons/Support.svg'
import ConsumersIcon from '../../assets/siderbar_icons/Consumers.svg'
import Checkmark from '../../assets/siderbar_icons/Checkmark.svg'
import File from '../../assets/siderbar_icons/File.svg'
import { useTableStore } from '@/store/table/table'
import { useSitesStore } from '@/store/sitesStore'

interface SidebarProps {
  onSidebarChange?: (isCollapsed: boolean) => void
}

const Sidebar: React.FC<SidebarProps> = ({ onSidebarChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [clickedMenuItem, setClickedMenuItem] = useState<string | null>(null)
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false)
  const { setFilteredRowsStore } = useTableStore()

  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { mode, toggleTheme, langKey, toggleLanguage } = useAppTheme()
  const { logout, user } = useAuth()
  const { selectedSite } = useSitesStore()
  const isDarkMode = mode === 'dark'

  // The page margin changes once per toggle, so the page never reflows while the tray glides.
  // A close reports at once, so the page widens under the shrinking tray.
  // An open reports after the glide, so the page narrows once the tray is wide.
  useEffect(() => {
    if (isCollapsed) {
      onSidebarChange?.(true)
      return
    }
    const timer = setTimeout(() => onSidebarChange?.(false), SIDEBAR_TRANSITION_MS)
    return () => clearTimeout(timer)
  }, [isCollapsed, onSidebarChange])

  // Reset clicked menu item when route changes
  useEffect(() => {
    const activeMenu = getActiveMenu()
    if (activeMenu && activeMenu !== clickedMenuItem) {
      setClickedMenuItem(activeMenu)
    }
  }, [location.pathname])

  const toggleSidebar = () => {
    const newState = !isCollapsed
    setIsCollapsed(newState)
  }

  const handleLogoutConfirm = () => {
    setLogoutConfirmationOpen(false)
    logout()
    navigate('/login')
  }

  const handleMenuClick = (menuId: string) => {
    if (!selectedSite) return
    const siteId = selectedSite.siteId
    setClickedMenuItem(menuId)

    switch (menuId) {
      case 'home':
        navigate(`/${siteId}/home`)
        break
      case 'consumers':
        navigate(`/${siteId}/consumers`)
        break
      case 'maps':
        navigate(`/${siteId}/maps`)
        break
      case 'history':
        navigate(`/${siteId}/history`)
        break
      case 'notifications':
        navigate(`/${siteId}/notifications`)
        break
      case 'analytics':
        navigate(`/${siteId}/analytics`)
        break
      case 'crm':
        navigate(`/${siteId}/crm`)
        break
      case 'reportGeneration':
        navigate(`/${siteId}/reportGeneration`)
        break
      case 'sensors':
        navigate(`/${siteId}/sensors`)
        break
      case 'settings':
        navigate(`/${siteId}/settings`)
        break
      case 'support':
        navigate(`/${siteId}/support`)
        break
    }
    setFilteredRowsStore({})
  }

  const getActiveMenu = () => {
    const parts = location.pathname.split('/').filter(Boolean)
    if (parts.length === 0) return 'overview'
    
    // If the first part is a siteId (number), the menu is the second part
    const isSiteId = !isNaN(Number(parts[0]))
    const menu = isSiteId ? (parts[1] || 'home') : parts[0]

    switch (menu) {
      case 'overview':
        return 'overview'
      case 'home':
        return 'home'
      case 'consumers':
        return 'consumers'
      case 'maps':
        return 'maps'
      case 'analytics':
        return 'analytics'
      case 'crm':
        return 'crm'
      case 'reportGeneration':
        return 'reportGeneration'
      case 'history':
        return 'history'
      case 'notifications':
        return 'notifications'
      case 'sensors':
        return 'sensors'
      case 'settings':
        return 'settings'
      case 'support':
        return 'support'
      default:
        return 'overview'
    }
  }

  const mainFeatures = [
    {
      id: 'home',
      icon: HomeIcon,
      label: 'עמוד הבית',
    },
    {
      id: 'consumers',
      icon: ConsumersIcon,
      label: 'טבלת צרכנים',
    },
    {
      id: 'maps',
      icon: MapIcon,
      label: 'מפה',
    },
    {
      id: 'notifications',
      icon: NotificationsIcon,
      label: 'התראות',
    },
    {
      id: 'analytics',
      icon: CircleIcon,
      label: 'עצי מים',
    },
    {
      id: 'crm',
      icon: Checkmark,
      label: 'CRM',
    },
    {
      id: 'reportGeneration',
      icon: File,
      label: 'הפקת דוחות',
    },
    {
      id: 'history',
      icon: HistoryChangesIcon,
      label: 'היסטוריית החלפות',
    },
    {
      id: 'sensors',
      icon: SensorsIcon,
      label: 'חיישנים',
    },
  ]

  const systemSupport = [
    {
      id: 'settings',
      icon: SettingIcon,
      label: 'הגדרות',
    },
    {
      id: 'support',
      icon: SupportIcon,
      label: 'תמיכה',
    },
  ]


  const Darkcolors = {
    background: isDarkMode ? '#1D325A' : (theme.palette.background.sidebar || theme.palette.background.default),
    border: isDarkMode ? '#2d4a7c' : (theme.palette.sidebar?.border || theme.palette.divider),
    heText: isDarkMode ? '#FFFFFF' : '#003F61',
    heHover: isDarkMode ? '#2d4a7c' : '#72A4EB',
    dividerLine: isDarkMode ? '#2d4a7c' : '#e0e0e0',
  }

  return (
    <Box
      data-print-hide
      data-testid="sidebar-root"
      sx={{
        width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_OPEN_WIDTH,
        padding: 1,
        backgroundColor: Darkcolors.background,
        borderRight: `1px solid ${Darkcolors.border}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'fixed',
        right: 0,
        top: 0,
        zIndex: 1000,
        overflow: 'hidden',
        boxSizing: 'border-box',
        transition: `width ${SIDEBAR_TRANSITION}, background-color 0.3s ease`,
        ...reducedMotion,
        '@media print': { display: 'none' },
      }}
    >
      <SidebarHeader isDarkMode={isDarkMode} />

      <SidebarUserBlock
        userName={user?.name || user?.email || 'משתמש'}
        isCollapsed={isCollapsed}
        isDarkMode={isDarkMode}
        onLogoutClick={() => setLogoutConfirmationOpen(true)}
      />

      {/* Main content — flexbox distributes items evenly */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-evenly',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Main menu items */}
        {mainFeatures.map(item => (
          <SidebarMenuItem
            id={item.id}
            key={item.id}
            icon={item.icon}
            label={item.label}
            isCollapsed={isCollapsed}
            isActive={getActiveMenu() === item.id}
            isClicked={clickedMenuItem === item.id}
            isDarkMode={isDarkMode}
            onClick={() => handleMenuClick(item.id)}
          />
        ))}
      </Box>

      {/* Bottom section - Settings & Support with divider */}
      <Box
        sx={{
          borderTop: `1px solid ${Darkcolors.dividerLine}`,
          pt: 1,
          mt: 'auto',
        }}
      >
        {/* Settings and Support */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
          {systemSupport.map(item => (
            <SidebarMenuItem
              id={item.id}
              key={item.id}
              icon={item.icon}
              label={item.label}
              isCollapsed={isCollapsed}
                isActive={getActiveMenu() === item.id}
              isClicked={clickedMenuItem === item.id}
              isDarkMode={isDarkMode}
              onClick={() => handleMenuClick(item.id)}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              boxSizing: 'border-box',
              width: '100%',
              pr: ICON_INSET,
            }}
          >
            <Box
              data-testid="sidebar-lang"
              onClick={toggleLanguage}
              sx={{
                width: ICON_CELL,
                flexShrink: 0,
                borderRadius: 1,
                fontSize: 16,
                fontWeight: 400,
                color: Darkcolors.heText,
                textAlign: 'center',
                fontFamily:
                  'Rubik, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {langKey.toUpperCase()}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: '4px' }}>
            <ThemeToggle isDarkMode={isDarkMode} onThemeToggle={toggleTheme} />
          </Box>
        </Box>

        <Box sx={{ borderTop: `1px solid ${Darkcolors.dividerLine}`, pt: 1 }}>
          <SidebarCollapseButton
            isCollapsed={isCollapsed}
            isDarkMode={isDarkMode}
            onToggle={toggleSidebar}
          />
        </Box>
      </Box>

      <Dialog
        open={logoutConfirmationOpen}
        onClose={() => setLogoutConfirmationOpen(false)}
        dir="rtl"
        PaperProps={{
          sx: {
            width: 'min(92vw, 420px)',
            bgcolor: isDarkMode ? '#202B40' : '#FFFFFF',
            color: isDarkMode ? '#FFFFFF' : '#003F61',
            border: `1px solid ${isDarkMode ? '#445876' : '#E4E7EB'}`,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Rubik, sans-serif', fontWeight: 600 }}>
          אישור יציאה
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Rubik, sans-serif', color: 'inherit' }}>
            האם אתה בטוח שברצונך להתנתק מהמערכת?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setLogoutConfirmationOpen(false)}
            sx={{
              color: isDarkMode ? '#BBD7FF' : '#003F61',
              textTransform: 'none',
            }}
          >
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleLogoutConfirm}
            sx={{
              bgcolor: '#DC2626',
              color: '#FFFFFF',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            התנתק
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Sidebar
